import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, Modal, message } from 'antd';
import PageLoader from '../../../../../../components/PageLoader/PageLoader';
import Card from '../../../../../../components/Card/Card';
import {
  fetchScenarios,
  fetchSubmissionDetail,
  saveSubmissionAnswers,
  submitScenarioSubmission,
  cancelScenarioSubmission,
} from '../../TestingExercisingAction';
import {
  scenarioByIdSelector,
  industryNameSelector,
  industriesSelector,
  usingMockSelector,
  submissionDetailSelector,
  loadingSubmissionDetailSelector,
  loadingScenariosSelector,
  submissionRequestSelector,
} from '../../TestingExercisingSelectors';
import { planIdSelector, planExpiryDateSelector } from '../../../Plan/PlanSelectors';
import { getPlanExpiryStatus } from '../../../../../../components/PlanExpiryBadge/plan-expiry-shared';
import { MSG_ERROR, MSG_SAVE_SUCCESS } from '../../../../../../constants';
import { getApiErrorMessage } from '../../../../../../utils/httpError';
import { MOCK_INDUSTRIES } from '../../mockData';
import {
  getIncidentBeats,
  resolveActiveIncidentLogEntry,
  resolveVideoAtSeconds,
} from '../../utils/incidentLog';
import { buildAnswerMap, flattenVideoQuestions, isQuestionRequired } from '../../utils/questions';
import { formatConclusionText, mergeWalkthroughVideos } from '../../utils/walkthroughVideos';
import VideoPlayer from '../ScenarioVideoPlayer/ScenarioVideoPlayer';
import MockBanner from '../MockBanner/MockBanner';
import WalkthroughHeader from './WalkthroughHeader';
import IncidentLogAside from './IncidentLogAside';
import IndustryHandoutCard from './IndustryHandoutCard';
import QuestionsCard from './QuestionsCard';
import WalkthroughFooter from './WalkthroughFooter';

function ScenarioWalkthrough({
  match,
  history,
  planId,
  planExpiry,
  scenario,
  industryName,
  industries,
  usingMock,
  submissionDetail,
  loadingDetail,
  loadingScenarios,
  submissionRequest,
  fetchScenarios,
  fetchSubmissionDetail,
  saveSubmissionAnswers,
  submitScenarioSubmission,
  cancelScenarioSubmission,
  accentColor,
}) {
  const submissionId = Number(match.params.submissionId);
  const headingRef = useRef(null);
  const [videoIndex, setVideoIndex] = useState(0);
  const [seekSeconds, setSeekSeconds] = useState(null);
  const [seekNonce, setSeekNonce] = useState(0);
  const [pinnedLogEntryId, setPinnedLogEntryId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [dirty, setDirty] = useState(false);
  const [playbackSeconds, setPlaybackSeconds] = useState(0);
  const industryOptions = usingMock && !industries.length ? MOCK_INDUSTRIES : industries;
  const [selectedIndustryId, setSelectedIndustryId] = useState(industryOptions[0]?.id);

  const load = useCallback(async () => {
    if (!planId || !submissionId) return;
    try {
      await Promise.all([fetchScenarios(planId), fetchSubmissionDetail(planId, submissionId)]);
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  }, [planId, submissionId, fetchScenarios, fetchSubmissionDetail]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!submissionDetail) return;
    setAnswers(buildAnswerMap(submissionDetail));
    setDirty(false);
  }, [submissionDetail]);

  useEffect(() => {
    if (!industryOptions.length) return;
    const matchByName = industryOptions.find(
      (item) => item.name && industryName && item.name.toLowerCase() === String(industryName).toLowerCase(),
    );
    setSelectedIndustryId(matchByName?.id || industryOptions[0].id);
  }, [industryOptions, industryName]);

  const videos = useMemo(
    () =>
      mergeWalkthroughVideos(scenario?.videos, submissionDetail?.videos, {
        useMockDefaults: usingMock,
      }),
    [scenario?.videos, submissionDetail?.videos, usingMock],
  );
  const currentVideo = videos[videoIndex] || null;
  const stepCount = Math.max(videos.length, 1);
  const isFirstVideo = videoIndex <= 0;
  const isLastVideo = videoIndex >= videos.length - 1;
  const status = String(submissionDetail?.status || '').toLowerCase();
  const isSubmitted = status.includes('submit') || status === 'completed';
  const isCancelled = status.includes('cancel');
  const isPlanExpired = getPlanExpiryStatus(planExpiry) === 'expired';
  const isReadOnly = isSubmitted || isCancelled;
  const isLocked = isReadOnly || isPlanExpired;
  const canLifecycle = !isReadOnly;

  const incidentBeats = useMemo(() => getIncidentBeats(currentVideo), [currentVideo]);
  const pauseMarks = useMemo(
    () => incidentBeats.map((beat) => resolveVideoAtSeconds(beat)).filter((value) => value != null),
    [incidentBeats],
  );
  const currentQuestions = useMemo(() => flattenVideoQuestions(currentVideo), [currentVideo]);
  const incidentLog = currentVideo?.incidentLog || [];
  const startSeconds = Number(currentVideo?.startSeconds) || 0;

  const activeLogEntry = useMemo(
    () => resolveActiveIncidentLogEntry(incidentLog, playbackSeconds, pinnedLogEntryId),
    [incidentLog, playbackSeconds, pinnedLogEntryId],
  );

  const pauseLabel = activeLogEntry?.time || null;

  useEffect(() => {
    setSeekSeconds(null);
    setSeekNonce(0);
    setPinnedLogEntryId(null);
    setPlaybackSeconds(startSeconds);
  }, [currentVideo?.videoId, startSeconds]);

  useEffect(() => {
    if (pinnedLogEntryId == null) return;
    const pinned = incidentLog.find((entry) => String(entry.id) === String(pinnedLogEntryId));
    const pinnedAt = resolveVideoAtSeconds(pinned);
    if (pinnedAt == null || playbackSeconds + 0.35 < pinnedAt || playbackSeconds > pinnedAt + 0.35) {
      setPinnedLogEntryId(null);
    }
  }, [incidentLog, pinnedLogEntryId, playbackSeconds]);

  const selectedIndustry = industryOptions.find((item) => item.id === selectedIndustryId) || industryOptions[0] || null;
  const handoutPdf = selectedIndustry?.pdf || scenario?.pdf;

  const answeredCount = currentQuestions.filter((q) => String(answers[q.questionId] || '').trim()).length;
  const requiredUnanswered = currentQuestions.filter(
    (q) => isQuestionRequired(q) && !String(answers[q.questionId] || '').trim(),
  );
  const questionsReady = currentQuestions.length === 0 || requiredUnanswered.length === 0;
  const canContinue = isReadOnly || !isLastVideo || questionsReady;

  const onAnswerChange = (questionId, value) => {
    if (isLocked) return;
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setDirty(true);
  };

  const onJumpToLog = (entry) => {
    const at = resolveVideoAtSeconds(entry);
    if (at == null) return;
    setPinnedLogEntryId(entry.id != null ? entry.id : null);
    setSeekSeconds(at);
    setSeekNonce((prev) => prev + 1);
    setPlaybackSeconds(at);
  };

  const persistAnswers = async () => {
    const payload = Object.keys(answers).map((questionId) => ({
      questionId: Number(questionId),
      answerText: answers[questionId] || '',
    }));
    await saveSubmissionAnswers(planId, submissionId, payload);
    setDirty(false);
    if (!usingMock) await fetchSubmissionDetail(planId, submissionId);
  };

  const onSave = async () => {
    if (isLocked) return;
    try {
      await persistAnswers();
      message.success(MSG_SAVE_SUCCESS);
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const scrollToScenarioHeading = useCallback(() => {
    const el = headingRef.current;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goToVideo = async (index) => {
    if (!Number.isFinite(index) || index < 0 || index >= videos.length || index === videoIndex) return;
    try {
      if (dirty && !isLocked) await persistAnswers();
      setVideoIndex(index);
      scrollToScenarioHeading();
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const onPrevious = () => goToVideo(videoIndex - 1);

  const onContinue = async () => {
    if (!canContinue) return;
    try {
      if (dirty && !isLocked) await persistAnswers();

      if (isLastVideo) {
        if (isPlanExpired) return;
        if (canLifecycle) {
          await submitScenarioSubmission(planId, submissionId);
          message.success('Scenario walkthrough submitted.');
          if (!usingMock) await fetchSubmissionDetail(planId, submissionId);
        }
        history.push('/dashboard/testing-exercising');
        return;
      }

      setVideoIndex((prev) => prev + 1);
      scrollToScenarioHeading();
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const onCancel = () => {
    if (isPlanExpired) return;
    Modal.confirm({
      title: 'Cancel this walkthrough?',
      content:
        'Progress will be kept for reference, but this attempt cannot be submitted. Start a new walkthrough if you want to run the exercise again.',
      okText: 'Cancel walkthrough',
      okType: 'danger',
      cancelText: 'Keep editing',
      onOk: async () => {
        try {
          await cancelScenarioSubmission(planId, submissionId);
          message.success('Submission cancelled.');
          history.push('/dashboard/testing-exercising');
        } catch (err) {
          message.error(getApiErrorMessage(err, MSG_ERROR));
          throw err;
        }
      },
    });
  };

  const backToScenarios = () => history.push('/dashboard/testing-exercising');

  if ((loadingDetail || loadingScenarios) && !submissionDetail) return <PageLoader />;
  if (!submissionDetail || (!scenario && !videos.length)) {
    return (
      <div className="testing-exercising-walkthrough">
        <p>Submission not found.</p>
        <Button onClick={backToScenarios}>Back to scenarios</Button>
      </div>
    );
  }

  const heading = submissionDetail.scenarioHeading || scenario?.heading || 'Scenario walkthrough';
  const overlay = currentVideo?.stageOverlay;
  const previousVideo = !isFirstVideo ? videos[videoIndex - 1] : null;
  const nextVideo = !isLastVideo ? videos[videoIndex + 1] : null;
  const previousLabel = previousVideo
    ? `Previous · ${previousVideo.heading || `Video ${videoIndex}`}`
    : 'Previous video';
  const nextLabel = isLastVideo
    ? canLifecycle
      ? 'Submit walkthrough'
      : 'Back to scenarios'
    : `Next · ${nextVideo?.heading || `Video ${videoIndex + 2}`}`;
  const unlockHint = isPlanExpired
    ? 'This plan has expired. Renew the subscription before saving or submitting.'
    : isReadOnly
    ? isCancelled
      ? 'This walkthrough was cancelled. Answers are locked — start a new walkthrough to run the exercise again.'
      : 'This walkthrough is submitted. Answers are locked as the official record — start a new walkthrough for another attempt.'
    : questionsReady
      ? isLastVideo
        ? 'Ready to submit this walkthrough.'
        : `Next up: ${nextVideo?.heading || `Video ${videoIndex + 2}`}.`
      : `Answer all required questions (${answeredCount}/${currentQuestions.length}) before you can submit.`;
  const startingParagraphs = formatConclusionText(submissionDetail.startingText);
  const conclusionParagraphs = formatConclusionText(submissionDetail.conclusionText);

  return (
    <div className="testing-exercising-walkthrough">
      {usingMock ? (
        <MockBanner>
          Demo data is active because the API returned no scenarios (or mock is forced). Set{' '}
          <code>REACT_APP_TESTING_EXERCISING_MOCK=false</code> to disable fallbacks.
        </MockBanner>
      ) : null}

      <WalkthroughHeader
        heading={heading}
        headingRef={headingRef}
        currentVideo={currentVideo}
        videoIndex={videoIndex}
        stepCount={stepCount}
        videos={videos}
        accentColor={accentColor}
        participant={submissionDetail.participant}
        statusLabel={submissionDetail.status}
        isCancelled={isCancelled}
        isSubmitted={isSubmitted}
        onBack={backToScenarios}
        onGoToVideo={goToVideo}
      />

      {/* Keyed on the step so the whole stage re-enters softly when the video changes. */}
      <div className="testing-exercising-walkthrough__stage" key={videoIndex}>
        {videoIndex === 0 && startingParagraphs.length ? (
          <Card
            className="testing-exercising-card"
            color={accentColor}
            title="Before you begin"
            description="Facilitator introduction for this exercise."
          >
            <div className="testing-exercising-card-body testing-exercising-prose">
              {startingParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </Card>
        ) : null}

        <Card
          className="testing-exercising-card"
          color={accentColor}
          title="Scenario media"
          description={
            incidentLog.length
              ? 'Play the clip, or click an incident-log bookmark to jump to that section. The video pauses at each mark.'
              : 'Play the clip for this stage of the walkthrough.'
          }
        >
          <div className="testing-exercising-card-body">
            <section
              className={`testing-exercising-media${incidentLog.length ? '' : ' is-logless'}`}
              aria-label="Scenario media"
            >
              <div className="testing-exercising-media__stage">
                <div className="testing-exercising-media__video">
                  <VideoPlayer
                    video={currentVideo}
                    overlay={overlay}
                    onTimeChange={setPlaybackSeconds}
                    pauseMarks={pauseMarks}
                    seekSeconds={seekSeconds}
                    seekNonce={seekNonce}
                    pauseLabel={pauseLabel}
                  />
                </div>
                <p className="testing-exercising-media__caption">
                  {currentVideo?.description || 'Press Play to start this stage.'}
                  {currentVideo?.videoTiming ? (
                    <span className="testing-exercising-media__timing">
                      {' '}
                      Suggested length: {currentVideo.videoTiming}
                    </span>
                  ) : null}
                </p>
              </div>

              <IncidentLogAside
                incidentLog={incidentLog}
                activeLogEntry={activeLogEntry}
                onJumpToLog={onJumpToLog}
              />
            </section>
          </div>
        </Card>

        <IndustryHandoutCard
          accentColor={accentColor}
          industryOptions={industryOptions}
          selectedIndustryId={selectedIndustryId}
          selectedIndustry={selectedIndustry}
          handoutPdf={handoutPdf}
          onSelectIndustry={setSelectedIndustryId}
        />

        <QuestionsCard
          accentColor={accentColor}
          isReadOnly={isLocked}
          currentQuestions={currentQuestions}
          answers={answers}
          answeredCount={answeredCount}
          activeLogEntry={activeLogEntry}
          facilitatorNote={currentVideo?.facilitatorNote}
          onAnswerChange={onAnswerChange}
        />

        {isLastVideo && conclusionParagraphs.length ? (
          <Card
            className="testing-exercising-card"
            color={accentColor}
            title="Conclusion"
            description="Facilitator close-out for this exercise."
          >
            <div className="testing-exercising-card-body testing-exercising-prose">
              {conclusionParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </Card>
        ) : null}
      </div>

      <WalkthroughFooter
        videoIndex={videoIndex}
        stepCount={stepCount}
        currentVideo={currentVideo}
        unlockHint={unlockHint}
        canLifecycle={canLifecycle}
        isFirstVideo={isFirstVideo}
        isLastVideo={isLastVideo}
        videosLength={videos.length}
        previousLabel={previousLabel}
        nextLabel={nextLabel}
        previousHeading={previousVideo?.heading}
        canContinue={canContinue && !(isPlanExpired && isLastVideo && canLifecycle)}
        planExpired={isPlanExpired}
        dirty={dirty}
        submissionRequest={submissionRequest}
        onCancel={onCancel}
        onSave={onSave}
        onPrevious={onPrevious}
        onContinue={onContinue}
        onBack={backToScenarios}
      />
    </div>
  );
}

const mapStateToProps = (state, ownProps) => ({
  planId: planIdSelector(state),
  planExpiry: planExpiryDateSelector(state),
  scenario: scenarioByIdSelector(state, ownProps.match.params.scenarioId),
  industryName: industryNameSelector(state),
  industries: industriesSelector(state),
  usingMock: usingMockSelector(state),
  submissionDetail: submissionDetailSelector(state),
  loadingDetail: loadingSubmissionDetailSelector(state),
  loadingScenarios: loadingScenariosSelector(state),
  submissionRequest: submissionRequestSelector(state),
});

const mapDispatchToProps = {
  fetchScenarios,
  fetchSubmissionDetail,
  saveSubmissionAnswers,
  submitScenarioSubmission,
  cancelScenarioSubmission,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ScenarioWalkthrough));
